import React from 'react';
import { Button, Card, CardContent, Chip, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import {
  INSTALL_RISK_WINDOW_DAYS,
  type InstallRiskRow,
  type PendingClosureRow,
  type ReadyToBookRow,
} from './workspaceDashboard.model';

function formatDayLabel(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

function daysAgo(days: number): string {
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function daysAhead(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

type GateRow = {
  projectId: string;
  workspace: string;
  customer: string;
  /** What happened or is due, e.g. "fitted 3 Oct 2026 · 4 days ago". */
  detail: string;
  /** Empty means the row is ready to move on. */
  holdUps: string[];
  href: string;
};

/**
 * One gate between workspace stages: a trigger, the rows waiting at it, what blocks each
 * one, and the tab that clears it. Renders nothing when no workspace is waiting.
 */
function GateCard({ title, description, rows, readyLabel, action, holdUpColor = 'warning' }: {
  title: string;
  description: string;
  rows: GateRow[];
  /** Chip for a row with no hold-ups; omitted when every row is a problem. */
  readyLabel?: string;
  action: string;
  holdUpColor?: 'warning' | 'danger';
}) {
  if (!rows.length) return null;
  const ready = readyLabel ? rows.filter((row) => row.holdUps.length === 0).length : 0;
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography level="title-lg">{title} ({rows.length})</Typography>
          {ready > 0 && (
            <Typography level="body-xs" color="success" fontWeight="lg">{ready} {readyLabel!.toLowerCase()}</Typography>
          )}
        </Stack>
        <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1.25 }}>{description}</Typography>
        <Stack gap={0.75}>
          {rows.map((row) => (
            <Stack
              key={row.projectId}
              direction={{ xs: 'column', sm: 'row' }}
              gap={0.5}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack gap={0.15} sx={{ minWidth: 0 }}>
                <Typography level="body-sm" fontWeight="lg" component={Link} to={row.href} sx={{ textDecoration: 'none' }}>
                  {row.workspace}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  {row.customer} · {row.detail}
                </Typography>
              </Stack>
              <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                {row.holdUps.length === 0 && readyLabel ? (
                  <Chip size="sm" color="success" variant="soft">{readyLabel}</Chip>
                ) : row.holdUps.map((holdUp) => (
                  <Chip key={holdUp} size="sm" color={holdUpColor} variant="soft">{holdUp}</Chip>
                ))}
                <Button size="sm" variant="plain" component={Link} to={row.href}>{action}</Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Bookings in the next fortnight with order, deposit or crew still missing, soonest first. */
export function InstallsAtRiskCard({ rows }: { rows: InstallRiskRow[] }) {
  return (
    <GateCard
      title="Installs at risk"
      description={`Booked in the next ${INSTALL_RISK_WINDOW_DAYS} days with the order, deposit or crew still missing. Soonest first.`}
      rows={rows.map((row) => ({
        ...row,
        detail: `${row.kind === 'delivery' ? 'delivery' : 'on site'} ${formatDayLabel(row.nextDay)} · ${daysAhead(row.daysUntil)}`,
      }))}
      action="Open"
      holdUpColor="danger"
    />
  );
}

/** Jobs whose installation (or delivery) day has passed but which are not closed yet, oldest first. */
export function PendingClosureCard({ rows }: { rows: PendingClosureRow[] }) {
  return (
    <GateCard
      title="Pending completion"
      description="The installation (or delivery) date has passed but the workspace isn't marked complete. Oldest first."
      rows={rows.map((row) => ({
        ...row,
        detail: `${row.kind === 'delivery' ? 'delivered' : 'fitted'} ${formatDayLabel(row.fittedOn)} · ${daysAgo(row.daysSince)}`,
      }))}
      readyLabel="Ready to close"
      action="Aftercare"
    />
  );
}

/** Ordered but not booked: production-dated jobs first, soonest date first, then the longest wait. */
export function ReadyToBookCard({ rows }: { rows: ReadyToBookRow[] }) {
  return (
    <GateCard
      title="Ready to book"
      description="Ordered but no installation (or delivery) date yet. Jobs with a production date come first, soonest first."
      rows={rows.map((row) => ({
        ...row,
        detail: row.productionDate
          ? `production ${formatDayLabel(row.productionDate)}`
          : row.orderedOn
            ? `ordered ${formatDayLabel(row.orderedOn)} · ${daysAgo(row.daysWaiting)}`
            : 'ordered',
      }))}
      readyLabel="Ready to book"
      action="Open"
    />
  );
}
